import { PopoverPosition } from 'react-tiny-popover';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { Paragraph } from 'src/shared/design-system/Text';
import { Tooltip } from 'src/shared/design-system/Tooltip';
import { useDispatch } from 'src/store';
import { decAddOnItemQuantity, incAddOnItemQuantity } from 'src/store/cart/actions';
import { ServerAddOnItem } from 'src/store/cart/types';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Spacing } from 'web-common/src/shared/styles';
import {
  imageSizePx,
  ItemImageContainer,
  ItemLeftColContainer,
  ItemRightColContainer,
  ItemRowContainer,
} from './MultiCartItem';
import { MultiCartQuantityPicker } from './MultiCartQuantityPicker';

const AddOnIconContainer = styled.div`
  border-radius: 4px;
  margin-right: 8px;
  flex-shrink: 0;
  background: #7b0f37;
  width: ${imageSizePx}px;
  height: ${imageSizePx}px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StyledTooltip = styled(Tooltip)`
  margin-left: ${Spacing.HALF};
`;

interface MultiCartAddOnItemProps {
  addOnItem: ServerAddOnItem;
  quantity: number;
  paddingX?: string;
  paddingY?: string;
}

export const MultiCartAddOnItem = ({
  addOnItem,
  quantity,
  paddingX = Spacing.NONE,
  paddingY = Spacing.NONE,
}: MultiCartAddOnItemProps) => {
  const { isSmallScreen } = useScreenContext();

  const dispatch = useDispatch();
  const increment = () => dispatch(incAddOnItemQuantity(false, { addOnItem }));
  const decrement = () => dispatch(decAddOnItemQuantity(false, { addOnItem }));

  const positions: PopoverPosition[] = isSmallScreen ? ['bottom'] : ['right', 'top', 'bottom', 'left'];

  return (
    <div className='bg-jackfruit-25' style={{ padding: `${paddingY} ${paddingX}` }}>
      <ItemRowContainer>
        <ItemLeftColContainer style={{ cursor: 'auto' }}>
          <ItemImageContainer>
            {addOnItem.imageUrl ? (
              <DynamicImage
                width={`${imageSizePx}px`}
                height={`${imageSizePx}px`}
                sourceHeight={imageSizePx}
                src={addOnItem.imageUrl ?? ''}
                alt={addOnItem.displayText ?? ''}
              />
            ) : (
              <AddOnIconContainer>
                <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M11.4669 11.7876C11.7587 12.0749 12.2174 12.0749 12.5093 11.7876L17.0127 7.10867C18.347 5.75423 18.2636 3.49684 16.8042 2.22449C15.5115 1.11631 13.5934 1.32153 12.4259 2.55284L11.9672 3.00431L11.5086 2.55284C10.341 1.32153 8.42293 1.11631 7.1303 2.22449C5.67088 3.49684 5.58749 5.75423 6.92181 7.10867L11.4669 11.7876ZM23.6843 15.3174C23.1422 14.5786 22.0998 14.4144 21.3492 14.948L16.3455 18.6009H11.3001C10.9665 18.6009 10.6746 18.2725 10.6746 17.9442C10.6746 17.6158 11.0082 17.3285 11.3001 17.3285H14.5942C15.2197 17.3285 15.8451 16.877 15.9702 16.2614C16.0953 15.4816 15.4698 14.7017 14.6359 14.7017H7.96426C6.88012 14.7017 5.71258 15.1122 4.83693 15.8099L2.91884 17.3285H0.625465C0.250186 17.3285 0 17.6569 0 17.9852V21.8433C0 22.2127 0.291884 22.5 0.625465 22.5H15.0529C15.9702 22.5 16.8876 22.2127 17.5964 21.6791L23.2673 17.6158C24.0596 17.0823 24.2264 16.0562 23.6843 15.3174Z'
                    fill='white'
                  />
                </svg>
              </AddOnIconContainer>
            )}
          </ItemImageContainer>
          <Paragraph size='16px' weight='600' margin='0' color={Colors.GRAY_DARK_30}>
            {addOnItem.displayText}
            {addOnItem.extraText && (
              <StyledTooltip content={addOnItem.extraText} showInfoCircle positions={positions} />
            )}
          </Paragraph>
        </ItemLeftColContainer>
        <ItemRightColContainer>
          <MultiCartQuantityPicker decrement={decrement} increment={increment} quantity={quantity} />
        </ItemRightColContainer>
      </ItemRowContainer>
    </div>
  );
};
